import * as React from 'react';
import { createContext, useState, useRef, PropsWithChildren } from 'react';
import { useQuery } from '@apollo/client';

import { ESTATE_QUERY } from '../../graphql/queries/estate';
import {
  AssignmentType,
  EstateInput,
  EstateQuery,
  EstateQueryVariables
} from '../../graphql/types';

import { useDsRouteParams } from '../../hooks/useDsRouteParams';
import { useMaxPolling } from '../../hooks/useMaxPolling';

import { TemplateLoader } from '../utils/TemplateLoader';

import { useAuthenticated } from '../../hooks/useAuthenticated';
import { UserRole } from '../../types/common';
import { utils } from '../../register';
import { useCachedSiteQuery } from '../../graphql/hooks/useCachedSiteQuery';
import { client } from '../../../client/graphql/client';
import Error from '../pages/Error';
import NotFound from '../pages/NotFound';

const { PreviewNotification } = utils;

interface ITemplateContext {
  assignmentTypeMapped: AssignmentType | undefined;
  isCorrection: boolean;
  isProject: boolean;
  isObjectPage: boolean;
  reload: () => void;
  reloading: boolean;
  maxPollingError: boolean;
  variables: any;
}

interface ITemplateContextProviderProps extends PropsWithChildren {
  isCorrection?: boolean;
  isPreview?: boolean;
  isDefaultRefresh?: boolean;
  isObjectPage?: boolean;
}

export const TemplateContext = createContext<ITemplateContext>({
  assignmentTypeMapped: undefined,
  isCorrection: false,
  isProject: false,
  isObjectPage: false,
  variables: null,
  maxPollingError: false,
  reload: () => {},
  reloading: false
});

const TemplateContextProvider: React.FC<ITemplateContextProviderProps> = React.memo(
  ({
    isCorrection = false,
    isPreview = false,
    isDefaultRefresh = false,
    isObjectPage = false,
    children
  }) => {
    const { brandId } = useCachedSiteQuery();
    const isRefreshingRef = useRef(isDefaultRefresh);
    const { role } = useAuthenticated();
    const [isRefreshing, setIsRefreshing] = useState(
      isDefaultRefresh && role !== UserRole.Seller
    );
    const { estateId, version } = useDsRouteParams();

    const input: EstateInput = {
      brandId,
      estateId: estateId ?? '',
      preview: isPreview,
      refresh: isRefreshing,
      version: version ?? undefined
    };

    if (!isObjectPage) Object.assign(input, { productId: 'ds' });
    const { startMaxPolling, stopMaxPolling, maxPollingError } = useMaxPolling();

    /* Main query */
    const { refetch, data, loading, error, startPolling, stopPolling } = useQuery<
      EstateQuery,
      EstateQueryVariables
    >(ESTATE_QUERY, {
      fetchPolicy: 'network-only',
      variables: {
        input,
        isObjectPage
      },
      ssr: true,
      onCompleted: () => {
        if (isRefreshingRef.current) setIsRefreshing(false);
        stopMaxPolling(() => {
          stopPolling();
          console.info('stop polling');
        });
      },
      onError: ({ graphQLErrors }) => {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.statusType === 'pending') {
            startMaxPolling(() => {
              console.info('start polling - initial');
              refetch({ input: { ...input, refresh: false } });
              startPolling(2500);
            });
          }
        });
      }
    });

    const reload = async () => {
      if (version) return;
      // Reset scroll position when refreshing data
      isRefreshingRef.current = true;
      setIsRefreshing(true);
      try {
        await client.query<EstateQuery, EstateQueryVariables>({
          query: ESTATE_QUERY,
          fetchPolicy: 'network-only',
          variables: {
            input: { ...input, refresh: true },
            isObjectPage
          }
        });
      } catch (err) {
        console.error(err.message);
      }

    };

    if (maxPollingError) {
      stopPolling();
      return <Error message="Kan ikke hente oppdrag. Vennligst forsøk igjen senere." />;
    }

    if (error?.graphQLErrors[0]?.extensions?.code === 'NO_REFRESH_ACCESS') {
      return <Error message="Ingen tilgang til å oppdatere data på oppdrag" />;
    }

    if (error?.graphQLErrors[0]?.extensions?.code === 'NOT_FOUND') {
      stopPolling();
      return <NotFound />;
    }

    if (
      error?.graphQLErrors[0]?.message === 'estate is not ready for public' ||
      error?.graphQLErrors[0]?.extensions?.code === 'NOT_READY_FOR_PUBLIC'
    ) {
      stopPolling();
      return <NotFound details="estate is not ready for public" />;
    }

    if (error && error?.graphQLErrors[0]?.extensions?.statusType !== 'pending') {
      stopPolling();
      return (
        <Error
          message={error.message}
          rcid={error?.graphQLErrors[0]?.extensions?.rcid as string}
        />
      );
    }

    const isLoading =
      isRefreshing ||
      loading ||
      (error && error?.graphQLErrors[0]?.extensions?.statusType === 'pending');

    if (isLoading) {
      return (
        <TemplateLoader
          text={
            isObjectPage
              ? 'Vennligst vent..'
              : typeof error?.graphQLErrors[0]?.extensions?.progressFeedback === 'string'
              ? error?.graphQLErrors[0]?.extensions?.progressFeedback
              : ``
          }
        />
      );
    }

    return (
      <TemplateContext.Provider
        value={{
          reload,
          assignmentTypeMapped: data?.estate?.assignmentTypeMapped,
          isCorrection,
          isProject: !!data?.estate?.isProjectMaster,
          isObjectPage: isObjectPage,
          reloading: isRefreshing || loading,
          maxPollingError,
          variables: { input }
        }}
      >
        {isPreview && isObjectPage ? <PreviewNotification /> : null}
        {children}
      </TemplateContext.Provider>
    );
  }
);

export default TemplateContextProvider;
